import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';
import api from '@/lib/api';

const MAX_PHOTOS = 4;
const MAX_BYTES = 5 * 1024 * 1024; // 5MB
const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const resolveUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') || url.startsWith('data:') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

/**
 * Drag-and-drop event photo uploader.
 * Props:
 *   value          – current array of photo URLs
 *   onChange(arr)  – called whenever the photo array changes
 *   testIdPrefix   – data-testid prefix (default 'event-photos')
 *   maxPhotos      – override max photos (default 4)
 */
export default function EventPhotoUploader({
  value = [],
  onChange,
  testIdPrefix = 'event-photos',
  maxPhotos = MAX_PHOTOS,
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const photos = Array.isArray(value) ? value : [];
  const remaining = Math.max(0, maxPhotos - photos.length);

  const validateFile = (file) => {
    if (!ACCEPTED.includes(file.type)) {
      toast.error(`${file.name}: please upload a JPG, PNG, or WEBP image`);
      return false;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name}: photo must be under 5MB`);
      return false;
    }
    return true;
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post('/upload/image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data?.url;
  };

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > remaining) {
      toast.error(`You can add up to ${remaining} more photo${remaining === 1 ? '' : 's'} (max ${maxPhotos} total)`);
      return;
    }
    const valid = files.filter(validateFile);
    if (!valid.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of valid) {
        const url = await uploadOne(f);
        if (url) urls.push(url);
      }
      onChange?.([...photos, ...urls]);
      toast.success(`${urls.length} photo${urls.length === 1 ? '' : 's'} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed — please try again');
    }
    setUploading(false);
  }, [photos, remaining, maxPhotos, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading) return;
    handleFiles(e.dataTransfer?.files);
  };

  const onPick = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const removeAt = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-uploader`}>
      {/* Drop Zone */}
      {photos.length < maxPhotos && (
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all px-6 py-8 flex flex-col items-center justify-center text-center
            ${dragOver ? 'border-teal bg-teal/5' : 'border-teal/40 bg-cream/30 hover:bg-teal/5'}
            ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
          data-testid={`${testIdPrefix}-dropzone`}
        >
          {uploading ? (
            <Loader2 size={28} className="text-teal animate-spin mb-2" />
          ) : (
            <Upload size={28} className="text-teal mb-2" />
          )}
          <p className="text-sm font-medium text-navy">
            {uploading ? 'Uploading…' : 'Drag & drop photos here or click to browse'}
          </p>
          <p className="text-xs text-text-mid mt-1">
            JPG, PNG, or WEBP · max 5MB each · up to {maxPhotos} photos
            {photos.length > 0 && <> · {remaining} slot{remaining === 1 ? '' : 's'} remaining</>}
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={onPick}
            className="hidden"
            data-testid={`${testIdPrefix}-input`}
          />
        </div>
      )}

      {/* Preview Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={`${testIdPrefix}-grid`}>
          {photos.map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              className="relative aspect-square rounded-xl overflow-hidden border border-border-subtle bg-cream/40 group"
              data-testid={`${testIdPrefix}-item-${idx}`}
            >
              <img
                src={resolveUrl(url)}
                alt={`Event photo ${idx + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
              />
              {idx === 0 && (
                <span className="absolute top-1 left-1 bg-teal text-white text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide">
                  Cover
                </span>
              )}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute top-1 right-1 bg-white/90 hover:bg-white text-red-600 rounded-full w-7 h-7 flex items-center justify-center shadow opacity-90 group-hover:opacity-100 transition-opacity"
                title="Remove photo"
                data-testid={`${testIdPrefix}-remove-${idx}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {photos.length === 0 && !uploading && (
        <p className="text-xs text-text-light flex items-center gap-1">
          <ImageIcon size={12} /> Photos are optional. Events without photos use the CMC branded placeholder.
        </p>
      )}
    </div>
  );
}
